import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Play, Trash2, Eye, RotateCcw } from "lucide-react";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PGFlowStatusBadge } from "@/components/shared/PGFlowStatusBadge";

interface TestRun {
	id: string;
	type: string;
	status: string;
	start_time: string;
	end_time: string | null;
	created_at: string;
	user_id: string;
	pgflow_status?: string | null;
	pgflow_remaining_steps?: number | null;
	pgflow_started_at?: string | null;
	pgflow_completed_at?: string | null;
	display_name?: string | null;
	profiles?: { display_name?: string | null } | null;
	pgflow_latest_step_slug?: string | null;
	pgflow_latest_step_status?: string | null;
	pgflow_last_error?: string | null;
	pgflow_active_worker_id?: string | null;
}

interface TestRunsTableProps {
	testRuns: TestRun[];
	onLaunch: (testRunId: string) => void;
	onDelete: (testRunId: string) => void;
	onViewResults?: (testRunId: string) => void;
	onRetry?: (testRunId: string) => void;
	isLaunching?: (testRunId: string) => boolean;
	isDeleting?: (testRunId: string) => boolean;
	isRetrying?: (testRunId: string) => boolean;
	readOnly?: boolean;
	contextLabel?: string; // "Project" or "Eval" - for future use
	tableTestId?: string;
}

const formatStepSlug = (slug: string) => slug.replace(/_/g, " ");
const summarizeWorker = (id: string) =>
	id.length > 8 ? `${id.slice(0, 8)}…` : id;

export const TestRunsTable = ({
	testRuns,
	onLaunch,
	onDelete,
	onViewResults,
	onRetry,
	isLaunching = () => false,
	isDeleting = () => false,
	isRetrying = () => false,
	readOnly = false,
	tableTestId,
}: TestRunsTableProps) => {
	const getStatusBadge = (status: string) => {
		const statusColors = {
			Prepared: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
			Running:
				"bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
			Evaluating:
				"bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
			Complete:
				"bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
			Failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
		};

		return (
			<span
				className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
					statusColors[status as keyof typeof statusColors] ||
					statusColors.Prepared
				}`}
			>
				{status}
			</span>
		);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const calculateDuration = (startTime: string, endTime: string | null) => {
		if (!endTime) return "—";
		const start = new Date(startTime).getTime();
		const end = new Date(endTime).getTime();
		const durationMs = end - start;
		const seconds = Math.floor(durationMs / 1000);
		const minutes = Math.floor(seconds / 60);
		const hours = Math.floor(minutes / 60);

		if (hours > 0) {
			return `${hours}h ${minutes % 60}m`;
		} else if (minutes > 0) {
			return `${minutes}m ${seconds % 60}s`;
		} else {
			return `${seconds}s`;
		}
	};

	const handleRowClick = (testRun: TestRun, event: React.MouseEvent) => {
		// Don't navigate if clicking on a button
		if ((event.target as HTMLElement).closest("button")) {
			return;
		}

		if (onViewResults && testRun.status === "Complete") {
			onViewResults(testRun.id);
		}
	};

	return (
		<div className="rounded-md border">
			<Table data-testid={tableTestId ?? "test-runs-table"}>
				<TableHeader>
					<TableRow>
						<TableHead>Type</TableHead>
						<TableHead>Started</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Flow Status</TableHead>
						<TableHead>Initiated By</TableHead>
						<TableHead>Duration</TableHead>
						<TableHead className="text-right">Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{testRuns.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={6}
								className="text-center text-muted-foreground py-8"
							>
								No test runs yet. Queue a run above to get started.
							</TableCell>
						</TableRow>
					) : (
						testRuns.map((testRun) => (
							<TableRow
								key={testRun.id}
								data-testid="test-run-row"
								data-run-id={testRun.id}
								onClick={(e) => handleRowClick(testRun, e)}
								className={
									onViewResults && testRun.status === "Complete"
										? "cursor-pointer hover:bg-muted/50"
										: ""
								}
							>
								<TableCell className="font-medium capitalize">
									{testRun.type}
								</TableCell>
								<TableCell>{formatDate(testRun.start_time)}</TableCell>
								<TableCell>{getStatusBadge(testRun.status)}</TableCell>
								<TableCell>
									<div className="flex flex-col gap-1">
										<PGFlowStatusBadge status={testRun.pgflow_status ?? null} />
										{typeof testRun.pgflow_remaining_steps === "number" &&
										testRun.pgflow_status ? (
											<span className="text-xs text-muted-foreground">
												{testRun.pgflow_remaining_steps} step
												{testRun.pgflow_remaining_steps === 1 ? "" : "s"}{" "}
												remaining
											</span>
										) : null}
										{testRun.pgflow_latest_step_slug ? (
											<span
												className="text-xs text-muted-foreground"
												data-testid="pgflow-latest-step"
											>
												Latest:{" "}
												{formatStepSlug(testRun.pgflow_latest_step_slug)}
												{testRun.pgflow_latest_step_status
													? ` (${testRun.pgflow_latest_step_status})`
													: ""}
											</span>
										) : null}
										{testRun.pgflow_active_worker_id ? (
											<span
												className="text-xs text-muted-foreground"
												data-testid="pgflow-active-worker"
											>
												Worker:{" "}
												{summarizeWorker(testRun.pgflow_active_worker_id)}
											</span>
										) : null}
										{testRun.pgflow_last_error ? (
											<span
												className="text-xs text-destructive"
												data-testid="pgflow-last-error"
											>
												Error: {testRun.pgflow_last_error}
											</span>
										) : null}
									</div>
								</TableCell>
								<TableCell>
									{testRun.display_name ??
										testRun.profiles?.display_name ??
										"Unknown"}
								</TableCell>
								<TableCell>
									{calculateDuration(testRun.start_time, testRun.end_time)}
								</TableCell>
								<TableCell className="text-right">
									<div className="flex justify-end gap-2">
										{testRun.status === "Complete" && onViewResults && (
											<Button
												variant="ghost"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													onViewResults(testRun.id);
												}}
												title="View Results"
											>
												<Eye className="h-4 w-4" />
											</Button>
										)}
										{testRun.status === "Prepared" && (
											<Button
												variant="ghost"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													onLaunch(testRun.id);
												}}
												disabled={readOnly || isLaunching(testRun.id)}
												title="Launch Test Run"
											>
												<Play className="h-4 w-4" />
											</Button>
										)}
										{testRun.status === "Failed" && onRetry && (
											<Button
												variant="ghost"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													onRetry(testRun.id);
												}}
												disabled={readOnly || isRetrying(testRun.id)}
												title="Retry Test Run"
											>
												<RotateCcw className="h-4 w-4" />
											</Button>
										)}
										<AlertDialog>
											<AlertDialogTrigger asChild>
												<Button
													variant="ghost"
													size="sm"
													disabled={readOnly || isDeleting(testRun.id)}
													title="Delete Test Run"
													onClick={(e) => e.stopPropagation()}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</AlertDialogTrigger>
											<AlertDialogContent>
												<AlertDialogHeader>
													<AlertDialogTitle>Delete Test Run</AlertDialogTitle>
													<AlertDialogDescription>
														Are you sure you want to delete this test run? This
														will also delete all associated test results. This
														action cannot be undone.
													</AlertDialogDescription>
												</AlertDialogHeader>
												<AlertDialogFooter>
													<AlertDialogCancel>Cancel</AlertDialogCancel>
													<AlertDialogAction
														onClick={(e) => {
															e.stopPropagation();
															onDelete(testRun.id);
														}}
														className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
													>
														Delete
													</AlertDialogAction>
												</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</div>
						</TableCell>
						</TableRow>
					))
					)}
				</TableBody>
			</Table>
		</div>
	);
};
